import { inject, Injectable } from '@angular/core';
import {
	FuseNavigationService,
	FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from '@microsoft/signalr';
import {
	ITaskSummary,
	TaskNotificationStore,
} from 'app/store/task-notification.store';
import { environment } from 'environments/environment';
import { NotificationService } from './notification/notification.service';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';

export interface ITaskNotification {
	id: number;
	title: string;
	description: string;
	createdBy: number;
	assignedTo: number;
	isCompleted: boolean;
	isNotified: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class TaskNotificationService {
	private readonly taskNotificationStore = inject(TaskNotificationStore);
	private readonly _fuseNavigationService = inject(FuseNavigationService);
	private readonly _notificationService = inject(NotificationService);
	private readonly _router = inject(Router);
	private readonly _auth0 = inject(AuthService);

	private hubConnection: HubConnection;

	async init() {
		await this.startSignalRConnection();
		this.setupSignalRListeners();
	}

	private async startSignalRConnection(): Promise<void> {
		try {
			this.taskNotificationStore.setHubStatus('connecting');

			this.hubConnection = new HubConnectionBuilder()
				.withUrl(`${environment.apiUrl}/hubs/taskNotification`, {
					accessTokenFactory: () =>
						firstValueFrom(this._auth0.getAccessTokenSilently()),
				})
				.withAutomaticReconnect()
				.withStatefulReconnect()
				.configureLogging(
					environment.env == 'dev' ? LogLevel.None : LogLevel.Trace
				)
				.build();

			// Set up connection state change handlers
			this.hubConnection.onreconnecting((error) => {
				this.taskNotificationStore.setHubStatus('connecting');
			});

			this.hubConnection.onreconnected((connectionId) => {
				this.taskNotificationStore.setHubStatus('connected');
				this.hubConnection
					.invoke('JoinTenantGroup')
					.catch((err) => console.error(err.toString()));
			});

			this.hubConnection.onclose((error) => {
				this.taskNotificationStore.setHubStatus('disconnected');
			});

			await this.hubConnection
				.start()
				.then(() => {
					this.hubConnection
						.invoke('JoinTenantGroup')
						.catch((err) => console.error(err.toString()));
				})
				.catch((err) =>
					console.error('Error establishing connection: ' + err)
				);

			this.taskNotificationStore.setHubStatus('connected');
		} catch (err) {
			this.taskNotificationStore.setHubStatus('disconnected');
		}
	}

	private setupSignalRListeners(): void {
		this.hubConnection.on(
			'TaskSummaryReceiver',
			(message: ITaskSummary) => {
				this.taskNotificationStore.setTaskSummary(message);

				const remainingTasksCount =
					this.taskNotificationStore.remainingTaskCount();
				const overdueTasksCount =
					this.taskNotificationStore.overdueTaskCount();
				const newTaskCount = this.taskNotificationStore.newTaskCount();

				this.updateNavigation(remainingTasksCount, overdueTasksCount);

				if (newTaskCount > 0) {
					this.showNotificationCount(newTaskCount);
				}
			}
		);

		this.hubConnection.on(
			'TaskUpdatesReceiver',
			(message: ITaskNotification) => {
				this.showNotification(message);
			}
		);
	}

	private updateNavigation(remainingTasks: number, overdueTasks: number) {
		// Get the component -> navigation data -> item
		const mainNavigationComponent =
			this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
				'mainNavigation'
			);

		// If the main navigation component exists...
		if (mainNavigationComponent) {
			const mainNavigation = mainNavigationComponent.navigation;
			const menuItem = this._fuseNavigationService.getItem(
				'tasks',
				mainNavigation
			);

			// If the menu item exists...
			if (menuItem) {
				// Update badge of the item
				if (overdueTasks > 0) {
					menuItem.badge = {
						title: `${overdueTasks} overdue`,
						classes: 'px-2 bg-red-500 text-white rounded-full',
					};
				} else {
					menuItem.badge = null;
				}

				// Update the subtitle of the item
				menuItem.subtitle = `${remainingTasks} remaining`;

				// Refresh the navigation
				mainNavigationComponent.refresh();
			}
		}
	}

	private showNotification(task: ITaskNotification) {
		let title = task.title;
		let type = 'info';
		let action = {
			text: 'View Task(s)',
			command: () => {
				this._router.navigateByUrl(`/tasks/${task.id}`);
			},
		};
		const isShowNotification = task.assignedTo != task.createdBy;

		if (!isShowNotification) return;

		if (task.isCompleted) {
			title = `Task completed: ${task.title}`;
			type = 'success';
			action = null;
		} else {
			title = `Task assigned to you: ${task.title}`;
		}

		let snackbarRef = this._notificationService.open({
			title,
			subtitle: task.description,
			type: task.isCompleted ? 'success' : 'info',
			actions: [action].filter((a) => a !== null),
		});

		if (task.isNotified !== true) {
			snackbarRef.afterDismissed().subscribe({
				next: () => this.hubConnection.invoke('TagAsNotified', task.id),
			});
		}
	}

	private showNotificationCount(count: number) {
		let snackbarRef = this._notificationService.open({
			title: `${count} new tasks!`,
			subtitle: 'You have new tasks that needs to be completed.',
			type: 'info',
			actions: [
				{
					text: 'View Task(s)',
					command: () => {
						if (this._router.url == '/tasks') return;

						this._router.navigateByUrl('/tasks');
					},
				},
			],
		});

		snackbarRef.afterDismissed().subscribe({
			next: () => this.hubConnection.invoke('TagAsNotified', null),
		});
	}
}
